<script lang="ts">
import { autoUpdate, computePosition, offset, size } from '@floating-ui/dom';
import { computed, onBeforeUnmount, onUpdated, ref, watch } from 'vue';

import PhxsgAutocompleteOptions from './phxsg-autocomplete-options.vue';

import type { PhxsgAutocompleteOption } from './phxsg-autocomplete-options.vue';

interface Props {
  id: string;
  open?: boolean;
  rootRef?: HTMLElement | null;
  options: PhxsgAutocompleteOption[];
  currentOption: string;
  label?: string;
  maxRows: number;
  minRows: number;
  noResultText: string;
  loading: boolean;
  loadingText: string;
  query: string;
  teleportTarget?: string | null;
  dropdownOffset?: number;
}
</script>

<script setup lang="ts">
const {
  open = false,
  rootRef = null,
  options,
  maxRows,
  minRows,
  teleportTarget = null,
  dropdownOffset = 4,
} = defineProps<Props>();

const emit = defineEmits<{
  'option-select': [option: PhxsgAutocompleteOption];
  'option-action': [option: PhxsgAutocompleteOption];
}>();

let cleanupFloating: (() => void) | null = null;
const containerHeight = ref(0);
const containerMaxHeight = ref(0);
const containerMinHeight = ref(0);
const containerRef = ref<HTMLDivElement | null>(null);

const dropdownStyle = computed(() => ({
  height: `${containerHeight.value}px`,
  maxHeight: containerMaxHeight.value ? `${containerMaxHeight.value}px` : undefined,
  minHeight: containerMinHeight.value ? `${containerMinHeight.value}px` : undefined,
}));

function updateHeight(): void {
  if (!containerRef.value) {
    return;
  }

  const optionElements = containerRef.value.querySelectorAll<HTMLElement>('.phxsg-autocomplete__option');

  let height = 0;
  let optionHeight = 0;

  for (const [index, option] of optionElements.entries()) {
    if (index > maxRows - 1 && maxRows !== -1) {
      break;
    }

    height += option.scrollHeight;
    optionHeight = option.scrollHeight;
  }

  if (minRows > 0 && options.length > 0) {
    containerMinHeight.value = optionHeight * minRows;
  }

  containerHeight.value = height;
}

function startFloating(): void {
  const container = containerRef.value;

  if (!rootRef || !container) {
    return;
  }

  cleanupFloating = autoUpdate(rootRef, container, () => {
    computePosition(rootRef, container, {
      middleware: [
        offset(dropdownOffset),
        size({
          apply({ availableHeight }) {
            containerMaxHeight.value = availableHeight - 30;
          },
        }),
      ],
    }).then(({ x, y }) => {
      if (!teleportTarget) {
        Object.assign(container.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      }
    });
  });
}

function stopFloating(): void {
  cleanupFloating?.();
  cleanupFloating = null;
}

watch(
  () => open,
  (isOpen) => {
    if (isOpen) {
      startFloating();
    } else {
      stopFloating();
    }
  },
);

onUpdated(() => {
  updateHeight();
});

onBeforeUnmount(stopFloating);
</script>

<template>
  <Teleport :to="teleportTarget" :disabled="teleportTarget === null">
    <div
      ref="containerRef"
      :class="[
        'phxsg-autocomplete__dropdown',
        {
          'phxsg-autocomplete__dropdown--relative': teleportTarget,
          'phxsg-autocomplete__dropdown--open': open,
        },
      ]"
      :style="dropdownStyle"
    >
      <Transition name="phxsg-autocomplete__dropdown-">
        <div v-if="open" class="phxsg-autocomplete__wrapper" data-simplebar>
          <PhxsgAutocompleteOptions
            :id="id"
            :label="label"
            :options="options"
            :current-option="currentOption"
            :no-result-text="noResultText"
            :loading="loading"
            :loading-text="loadingText"
            :query="query"
            @option-select="emit('option-select', $event)"
            @option-action="emit('option-action', $event)"
          >
            <template v-if="$slots.option" #option="scope">
              <slot name="option" v-bind="scope" />
            </template>

            <template v-if="$slots.empty" #empty="scope">
              <slot name="empty" v-bind="scope" />
            </template>

            <template v-if="$slots.loading" #loading="scope">
              <slot name="loading" v-bind="scope" />
            </template>
          </PhxsgAutocompleteOptions>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>
